import React, { useEffect, useMemo, useState } from 'react';

import { Badge, Button, InputSearch, Modal } from '@/components';
import {
  showErrorToast,
  showSuccessToast,
} from '@/components/toast/globalToast';
import {
  SiSlotDetail,
  SiSlotRider,
  SiStandbyAvailability,
  scheduledInstantAPI,
} from '@/services/api/scheduled-instant';

import { useDriversDropdown } from './useDriversDropdown';

interface Candidate {
  code: string;
  name: string;
  phone: string;
  isReserve: boolean;
}

interface StandbyModalProps {
  isOpen: boolean;
  onClose: () => void;
  slotId: string;
  /** e.g. "Wicked Pies · 22-06-2026 · 06:00–09:00" */
  slotLabel: string;
  /** The rider being replaced. */
  target: { id: string; riderName: string } | null;
  /** This slot's reserve (cadangan) riders — surfaced first. */
  reserves: SiSlotRider[];
  onReplaced: (detail: SiSlotDetail) => void;
}

const initials = (name: string) =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || '?';

const StandbyModal: React.FC<StandbyModalProps> = ({
  isOpen,
  onClose,
  slotId,
  slotLabel,
  target,
  reserves,
  onReplaced,
}) => {
  const drivers = useDriversDropdown(isOpen);
  const [search, setSearch] = useState('');
  const [avail, setAvail] = useState<Record<string, SiStandbyAvailability>>({});
  const [assigning, setAssigning] = useState<string | null>(null);

  // Reserve riders first, then searched drivers (minus any already a reserve).
  const candidates = useMemo<Candidate[]>(() => {
    const reserveCodes = new Set(reserves.map((r) => r.riderCode));
    const fromReserves: Candidate[] = reserves.map((r) => ({
      code: r.riderCode,
      name: r.riderName,
      phone: r.riderPhone,
      isReserve: true,
    }));
    const fromDrivers: Candidate[] = drivers.options
      .map((d) => ({
        code: d.code || `DRV${d.id}`,
        name: d.name || `${d.first_name ?? ''} ${d.last_name ?? ''}`.trim(),
        phone: d.phone || d.phone_number || d.phoneNumber || d.mobile || '',
        isReserve: false,
      }))
      .filter((c) => !reserveCodes.has(c.code));
    return [...fromReserves, ...fromDrivers];
  }, [reserves, drivers.options]);

  const codesKey = candidates.map((c) => c.code).join(',');

  // Batch availability annotation. Non-blocking — a failure just leaves the
  // rows un-annotated; the replace call re-validates server-side anyway.
  useEffect(() => {
    if (!isOpen || !candidates.length) {
      setAvail({});
      return;
    }
    let cancelled = false;
    scheduledInstantAPI
      .standbyCheck(
        slotId,
        candidates.map((c) => c.code)
      )
      .then((res) => {
        if (cancelled) return;
        const map: Record<string, SiStandbyAvailability> = {};
        for (const r of res) map[r.riderCode] = r;
        setAvail(map);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // codesKey captures the candidate set; slotId scopes it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, slotId, codesKey]);

  const assign = async (c: Candidate) => {
    if (!target) return;
    setAssigning(c.code);
    try {
      const detail = await scheduledInstantAPI.replaceRider(slotId, target.id, {
        riderCode: c.code,
        riderName: c.name,
        riderPhone: c.phone,
      });
      showSuccessToast(
        'Pengganti ditugaskan',
        `${c.name} menggantikan ${target.riderName}`
      );
      onReplaced(detail);
      onClose();
    } catch (e: any) {
      showErrorToast('Gagal menugaskan', e.message || 'Terjadi kesalahan');
    } finally {
      setAssigning(null);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !assigning && onClose()}
      icon="ri-user-search-line"
      iconTone="primary"
      title="Cari Pengganti"
      subtitle={
        target ? `${slotLabel} · mengganti ${target.riderName}` : slotLabel
      }
      width="lg"
    >
      <div className="space-y-3">
        <InputSearch
          placeholder="Cari rider berdasarkan nama…"
          value={search}
          onChange={(v) => {
            setSearch(v);
            drivers.setSearch(v);
          }}
          showClearButton
        />
        <p className="text-[11px] text-gray-400">
          Rider cadangan tampil di atas · rider tanpa jadwal bentrok / day-off
          bisa langsung ditugaskan.
        </p>

        <div className="max-h-[46vh] overflow-y-auto space-y-1.5 pr-0.5">
          {drivers.loading && candidates.length === 0 && (
            <p className="text-xs text-gray-400 py-4 text-center">Memuat…</p>
          )}
          {!drivers.loading && candidates.length === 0 && (
            <p className="text-xs text-gray-400 py-4 text-center">
              Tidak ada kandidat. Coba kata kunci lain.
            </p>
          )}

          {candidates.map((c) => {
            const a = avail[c.code];
            const unavailable = a && !a.available;
            return (
              <div
                key={c.code}
                className="flex items-center gap-2 bg-white border border-gray-200 rounded-gsm px-3 py-2"
              >
                <span className="w-7 h-7 rounded-full bg-(--theme-accent-alpha-10) text-accent text-[10px] font-bold flex items-center justify-center shrink-0">
                  {initials(c.name)}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-semibold text-ink truncate">
                      {c.name}
                    </span>
                    {c.isReserve && (
                      <Badge tone="primary" size="small">
                        ⭐ Cadangan
                      </Badge>
                    )}
                  </div>
                  <span className="font-mono text-[10px] text-gray-400">
                    {c.code}
                  </span>
                </div>

                {a === undefined ? (
                  <span className="text-[10px] text-gray-300">cek…</span>
                ) : a.available ? (
                  <Badge tone="success" size="small">
                    ✓ Tidak ada jadwal
                  </Badge>
                ) : a.reason === 'DAY_OFF' ? (
                  <Badge tone="amber" size="small">
                    Day-off
                  </Badge>
                ) : (
                  <Badge tone="danger" size="small">
                    Ada jadwal
                  </Badge>
                )}

                <Button
                  variant="outline-accent"
                  size="xs"
                  disabled={!!unavailable || !!assigning}
                  onClick={() => assign(c)}
                >
                  {assigning === c.code ? 'Menugaskan…' : 'Tugaskan'}
                </Button>
              </div>
            );
          })}

          {drivers.hasMore && (
            <button
              type="button"
              onClick={drivers.loadMore}
              disabled={drivers.loadingMore}
              className="w-full text-xs font-semibold text-accent py-2 hover:bg-(--theme-accent-alpha-10) rounded-gsm disabled:opacity-50"
            >
              {drivers.loadingMore ? 'Memuat…' : 'Muat lebih banyak'}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default StandbyModal;
